import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { PlatformStatsForDomainResponse } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { canShowModel } from '../../../../../shared/utils/utils';
import { getModelNameIcon, getModelNameLabel } from '../../../../../shared/helpers/ChatTypeHelpers';
import { TrendTag } from '../../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../../shared/utils/utils';
import './PlatformsTable.scss';

interface PlatformRow {
  rank: number;
  platform: string;
  icon: string | null;
  label: string;
  count: number;
  share: number;
  shareChange: number;
}

interface PlatformsTableProps {
  platformStats: PlatformStatsForDomainResponse | null;
  previousPlatformStats: PlatformStatsForDomainResponse | null;
  isLoading: boolean;
  features: string[];
}

const PlatformsTable: React.FC<PlatformsTableProps> = ({
  platformStats,
  previousPlatformStats,
  isLoading,
  features,
}) => {
  const { t } = useTranslation();

  const rows: PlatformRow[] = useMemo(() => {
    if (!platformStats) return [];

    const previousMap = new Map(
      (previousPlatformStats?.platforms ?? []).map((p) => [p.platform, p.share])
    );

    return platformStats.platforms
      .filter((p) => canShowModel(p.platform, features))
      .map((p, index) => ({
        rank: index + 1,
        platform: p.platform,
        icon: getModelNameIcon(p.platform),
        label: getModelNameLabel(p.platform),
        count: p.count,
        share: p.share,
        shareChange: p.share - (previousMap.get(p.platform) ?? 0),
      }));
  }, [platformStats, previousPlatformStats, features]);

  return (
    <div className="stats__panel platforms-table">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('platforms')}
          <CustomTooltip tooltipText="platformsTooltip" addMargin={false} />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
        </Skeleton>
      ) : rows.length === 0 ? (
        <div className="stats-box">
          <EmptyPanel size="xs" />
        </div>
      ) : (
        <DataTable
          value={rows}
          stripedRows
          tableClassName="table"
          className="table-wrapper"
          tableStyle={{ width: '100%' }}
          dataKey="platform"
        >
          <Column
            field="rank"
            header={t('rank')}
            headerClassName="table__header table__header--center platforms-table__rank-cell"
            bodyClassName="table__cell--center platforms-table__rank-cell"
            style={{ width: '50px', minWidth: '50px' }}
            body={(rowData: PlatformRow) => (
              <span className="platforms-table__rank">{rowData.rank}</span>
            )}
          />

          <Column
            field="platform"
            header={t('platform')}
            headerClassName="table__header"
            body={(rowData: PlatformRow) => (
              <div className="platforms-table__platform">
                {rowData.icon && (
                  <span className={`platforms-table__icon ico--sm ${rowData.icon}`} />
                )}
                <span className="platforms-table__label">{rowData.label}</span>
              </div>
            )}
          />

          <Column
            field="count"
            header={t('count')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '100px', minWidth: '100px' }}
            body={(rowData: PlatformRow) => (
              <span>{rowData.count}</span>
            )}
          />

          <Column
            field="share"
            header={t('share')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px', minWidth: '120px' }}
            body={(rowData: PlatformRow) => (
              <span>{rowData.share.toFixed(1)}%</span>
            )}
          />

          <Column
            field="shareChange"
            header={t('change')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px', minWidth: '120px' }}
            body={(rowData: PlatformRow) => (
              <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />
            )}
          />
        </DataTable>
      )}
    </div>
  );
};

export default PlatformsTable;
